'use client'

// libraries
import clsx from 'clsx'
import { useState, useEffect } from 'react'

// components
import Error from '/react4xp/components/Form/Error'

// utils
import { useFormField } from '/react4xp/utils/useFormField'

// svg
import { ChevronDown } from '/react4xp/components/Icons/Ux'

// css
import './index.scss'

// types
interface SelectProps {
	label?: string
	helper?: string
	id?: string
	name?: string
	size?: 'x-small' | 'small' | 'medium' | 'large' | 'full'
	disabled?: boolean
	options?: {
		label?: string
		value?: string
		disabled?: boolean
	}[]
	selected?: string
	error?: string
	invalid?: boolean
	required?: boolean
	onChange?: (value: string) => void
}

export default function Select({
	label,
	helper,
	id,
	name,
	size = 'full',
	disabled = false,
	options,
	selected,
	error: externalError,
	invalid: externalInvalid,
	required = false,
	onChange: externalOnChange
}: SelectProps) {
	const { finalError, finalInvalid, updateFormValue, getRegisterProps } = useFormField({
		name,
		externalError,
		externalInvalid,
		required
	})

	const [value, setValue] = useState<string>(selected || options?.[0]?.value || '')

	useEffect(() => {
		if (selected !== undefined) {
			setValue(selected)
		}
	}, [selected])

	const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const newValue = e.target.value
		setValue(newValue)
		externalOnChange?.(newValue)
		updateFormValue(newValue)
	}

	return (
		<div className={clsx(
			'select-component',
			finalInvalid && 'select-component--invalid'
		)}>

			{label && (
				<label className='select-component__label body'>
					{label}
				</label>
			)}

			{helper && (
				<p className='select-component__helper body-small'>
					{helper}
				</p>
			)}

			<div className={clsx(
				'select-component__select',
				size && `select-component__select--${size}`
			)}>

				<select
					{...getRegisterProps({
						validate: (value: string) => {
							if (required) {
								// check if the selected value corresponds to a disabled option
								const selectedOption = options?.find(option => option.value === value)
								if (!value || value === '' || selectedOption?.disabled) {
									return 'This field is required'
								}
							}
							return true
						}
					})}
					id={id}
					name={name}
					value={value}
					onChange={handleChange}
					disabled={disabled}
				>
					{options?.map((option) => (
						<option
							key={option.value}
							value={option.value}
							disabled={option.disabled}
						>
							{option.label}
						</option>
					))}
				</select>

				<ChevronDown />

			</div>

			{finalError && finalInvalid && (
				<Error label={finalError} />
			)}

		</div>
	)
}